import React, { useEffect, useState } from 'react';
import history from 'helpers/history';
import {
    FaCog,
    FaPowerOff,
    FaQuestionCircle,
    FaUser,
    FaBell,
    FaShieldAlt,
    FaMoon,
    FaSun,
    FaChevronRight,
    FaSignOutAlt,
} from 'react-icons/fa';
import User from 'models/UserModel';
import Avatar from 'react-avatar';
import store from 'store/store';
import actionTypes from 'helpers/actionTypes';
import Tenant from 'models/TenantModel';

interface Props {
    user: User;
    tenant: Tenant;
}

interface MenuItemProps {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    onClick: () => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ icon, title, subtitle, onClick }) => {
    return (
        <button
            onClick={onClick}
            className="w-full px-4 py-3 hover:bg-gray-50 transition-colors duration-150 text-left"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 text-gray-600 mt-0.5">{icon}</div>
                    <div>
                        <div className="font-semibold text-gray-900">{title}</div>
                        <div className="text-sm text-gray-500">{subtitle}</div>
                    </div>
                </div>
                <FaChevronRight className="w-3 h-3 text-gray-400" />
            </div>
        </button>
    );
};

const UserAvatar: React.FC<Props> = ({ user, tenant }: Props) => {
    const [userName, setUserName] = useState('');
    const [profilePanelExpanded, setProfilePanelExpanded] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
        setUserName(`${user?.firstName} ${user?.lastName}`);
        // Check for saved dark mode preference
        const savedDarkMode = localStorage.getItem('darkMode') === 'true';
        setIsDarkMode(savedDarkMode);
    }, []);

    const toggleDarkMode = () => {
        const newDarkMode = !isDarkMode;
        setIsDarkMode(newDarkMode);
        localStorage.setItem('darkMode', newDarkMode.toString());
        // You can implement actual dark mode logic here
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        store.dispatch({ type: actionTypes.DELETE_RECENTLY_VIEWED_ROS, payload: {} });

        history.push('/login');
        window.location.reload();
    };

    const onClickOutside = (e: any) => {
        const target = document.querySelector(`#user_avatar_dropdown`);
        const withinBoundaries = e.composedPath().includes(target);

        if (withinBoundaries) {
        } else {
            togglePicker(false);
        }
    };

    const togglePicker = (open: boolean) => {
        if (open) {
            setProfilePanelExpanded(true);
            document.addEventListener('click', onClickOutside);
        } else {
            setProfilePanelExpanded(false);
            document.removeEventListener('click', onClickOutside);
        }
    };

    const redirect = (toPath: string) => {
        history.push(toPath);
        togglePicker(false);
    };

    return (
        <div
            className="relative clickable  items-center space-x-3 pl-3 pr-8 border-l border-slate-600 hover:bg-slate-700 hover:border-slate-500 transition-all duration-200 ease-in-out"
            onClick={() => togglePicker(!profilePanelExpanded)}
        >
            <button className={` flex h-full items-center space-x-3 pl-3  border-slate-600  `}>
                <div className="text-right">
                    <div className="text-sm font-semibold text-white">{userName}</div>
                    <div className="text-xs text-slate-400">{user.tenant?.businessName}</div>
                </div>
                <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center border-2 border-blue-400">
                    <span className="text-sm font-bold text-white">
                        {userName
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()}
                    </span>
                </div>
            </button>
            {!!profilePanelExpanded && (
                <div
                    className="absolute top-16 right-0 z-50 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
                    id="user_avatar_dropdown"
                >
                    {/* Header with gradient background */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-6 text-white relative">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                                <span className="text-lg font-bold text-white">
                                    {userName
                                        .split(' ')
                                        .map((n) => n[0])
                                        .join('')
                                        .toUpperCase()}
                                </span>
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-lg">{userName}</h3>
                                <p className="text-blue-100 text-sm">{user?.email}</p>
                            </div>
                        </div>
                        <div className="mt-3 flex items-center space-x-2">
                            {user.isAdmin && (
                                <span className="bg-white bg-opacity-20 px-2 py-1 rounded-md text-xs font-semibold">
                                    Admin
                                </span>
                            )}
                            {!!tenant?.plan && (
                                <span className="bg-yellow-500 px-2 py-1 rounded-md text-xs font-semibold text-white">
                                    {tenant.plan.name} Plan
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                        {/* Dark Mode Toggle */}
                        {/* <div className="px-4 py-3 hover:bg-gray-50 transition-colors duration-150">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="w-5 h-5 text-gray-600">{isDarkMode ? <FaMoon /> : <FaSun />}</div>
                                    <span className="text-gray-800 font-semibold">Dark Mode</span>
                                </div>
                                <button
                                    onClick={toggleDarkMode}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                                        isDarkMode ? 'bg-blue-600' : 'bg-gray-300'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                                            isDarkMode ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>
                        </div> */}

                        {/* Menu Items */}
                        <MenuItem
                            icon={<FaUser />}
                            title="Profile & Account"
                            subtitle="Manage your personal information"
                            onClick={() => redirect('/settings/profile')}
                        />
                        <MenuItem
                            icon={<FaBell />}
                            title="Notifications"
                            subtitle="Customize your alerts"
                            onClick={() => redirect('/settings/notifications')}
                        />
                        <MenuItem
                            icon={<FaShieldAlt />}
                            title="Privacy & Security"
                            subtitle="Control your data and security"
                            onClick={() => redirect('/settings/security')}
                        />
                        <MenuItem
                            icon={<FaCog />}
                            title="Settings"
                            subtitle="System preferences and configuration"
                            onClick={() => redirect('/settings/companySettings')}
                        />
                        <MenuItem
                            icon={<FaQuestionCircle />}
                            title="Help & Support"
                            subtitle="Get help and contact support"
                            onClick={() => redirect('/help')}
                        />
                    </div>

                    {/* Sign Out Button */}
                    <div className="border-t border-gray-200 p-4">
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center justify-center space-x-2 py-2.5 px-4 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150 font-semibold"
                        >
                            <FaSignOutAlt className="w-4 h-4" />
                            <span>Sign Out</span>
                        </button>
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-3 bg-gray-50 text-center">
                        <p className="text-xs text-gray-500">Shop Link v2.1.0</p>
                        <p className="text-xs text-gray-400 mt-1">© 2025</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAvatar;
